/**
 * Delete-objective confirmation modal + the soft-delete write.
 *
 * `confirmDeleteObjective` retires the ObservationDefinition and (if
 * present) ActivityDefinition by setting status:retired. Goals
 * referencing the objective are untouched — they continue to resolve
 * via stable code per OMON-004; retired objectives just stop appearing
 * in catalogue tree views unless "Show retired" is toggled on.
 *
 * Extracted from main.ts as part of OMON-060 Phase 6c.4.
 */

import { doc, updateDoc } from "firebase/firestore";

import { state } from "../state";
import { beginEditSession } from "./detail";
import { db } from "../firebase";
import { modalContainer, statusEl } from "../dom";
import { escapeHtml } from "../utils/html";
import { renderCatalogue } from "./tree";

// ======================================================================
// Delete Objective
// ======================================================================

export function showDeleteModal(name: string, title: string) {
  modalContainer.innerHTML = `
    <div class="modal-overlay">
      <div class="modal-box">
        <h3>Delete goal</h3>
        <p>Are you sure you wish to delete <strong>${escapeHtml(title)}</strong>? This cannot be undone.</p>
        <div class="modal-actions">
          <button class="btn-outline" id="modalCancel">Cancel</button>
          <button class="btn-delete" id="modalConfirm">Yes, Delete</button>
        </div>
      </div>
    </div>
  `;

  modalContainer.querySelector(".modal-overlay")?.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).classList.contains("modal-overlay")) closeModal();
  });
  modalContainer.querySelector("#modalCancel")?.addEventListener("click", () => closeModal());
  modalContainer.querySelector("#modalConfirm")?.addEventListener("click", () => confirmDeleteObjective(name));
}

export function closeModal() {
  modalContainer.innerHTML = "";
}

export async function confirmDeleteObjective(name: string) {
  closeModal();
  try {
    // OMON-105: soft-delete writes BOTH `status: "retired"` (FHIR-canonical)
    // AND `active: false` (the field that `loadCatalogue` / `loadActivities`
    // filter on). Pre-OMON-105 we only wrote `status` — the loader ignored
    // it and the doc re-appeared after browser refresh. Both fields stay
    // for now; consolidating onto one is OMON-094 Phase 4 follow-up work.
    await updateDoc(doc(db, "_observationDefinition", name), {
      status: "retired",
      active: false,
    });
    try {
      await updateDoc(doc(db, "_activityDefinition", name), {
        status: "retired",
        active: false,
      });
    } catch { /* may not exist */ }

    state.catalogueData.delete(name);
    state.activityData.delete(name);
    beginEditSession(null);  // OMON-232: clear edit session via the single mutator
    state.catalogueEditMode = false;
    statusEl.textContent = `Deleted "${name}"`;
    renderCatalogue();
  } catch (err) {
    console.error("Failed to delete:", err);
    statusEl.textContent = "Error deleting -- check console";
  }
}
